import { CalendarDate, getLocalTimeZone } from "@internationalized/date";
import { useQueries } from "@tanstack/react-query";
import { AddOutline24Icon } from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { isFuture, isToday, isYesterday } from "date-fns";
import { format } from "date-fns/format";
import AddVehicle from "features/VehicleSidebar/VehicleList/AddVehicles/AddVehicle";
import { DayPicker } from "features/VehicleSidebar/VehicleList/AddVehicles/DayPicker";
import { useVehicleDate } from "features/VehicleSidebar/VehicleList/AddVehicles/useVehicleDate";
import { processTransportList } from "features/VehicleSidebar/VehicleList/AddVehicles/utils";
import { getVehicleListTabs } from "features/VehicleSidebar/VehicleList/AddVehicles/VehicleTabs";
import AlternativeTransportList from "features/VehicleSidebar/VehicleList/AlternativeTransportList/AlternativeTransportList";
import FilterButtons from "features/VehicleSidebar/VehicleList/common/FilterButtons";
import { SearchResultText } from "features/VehicleSidebar/VehicleList/common/SearchResultText";
import ReserveOrBackupAlternativeTransportList from "features/VehicleSidebar/VehicleList/ReserveAlternativeTransportList/ReserveOrBackupAlternativeTransportList";
import TrainList from "features/VehicleSidebar/VehicleList/TrainList/TrainList";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  DropsButton,
  DropsDatePicker,
  Field,
  HStack,
  SearchInput,
  Separator,
  Tabs,
  TabsList,
  TabsTrigger,
  VStack,
} from "shared/components/Spor";
import { SectionStateContent } from "shared/components/states/SectionStateContent";
import { reserveAlternativeTransportListResponseSchema as schema } from "shared/types/alternativeTransport";
import { getISODrivingFromToTimes } from "shared/utils/dateTimeUtils";
import styled, { useTheme } from "styled-components";
import * as z from "zod";

const ListContainer = styled.div<{ hasSearchQuery: boolean }>`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background: ${({ theme }) => theme.colorBackgroundMain};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colorOutlineSecondary};
  z-index: 2;
  top: 0;
  height: 100%;
`;

const ScrollBox = styled(Box)`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  overflow: scroll;
  -webkit-scrollbar: none; /* WebKit */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
`;

export const calendarDateToday = () => {
  const todayDate = format(new Date(), "yyyy-MM-dd");
  const [yyyy, MM, dd] = todayDate.split("-").map((it) => parseInt(it, 10));
  return new CalendarDate(yyyy, MM, dd);
};

const showOldVehiclesBtnStates = (selectedDate: Date) => {
  if (isYesterday(selectedDate)) {
    return { disable: true, overrideCheck: true };
  }

  if (isFuture(selectedDate)) {
    return { disable: true, overrideCheck: false };
  }
  return { disable: false, overrideCheck: false };
};

type VehicleListProps = {
  vehicleTabsIndex: number;
};

const VehicleList = ({ vehicleTabsIndex }: VehicleListProps) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchHits, setSearchHits] = useState(0);
  const [showTrainsMissingData, setShowTrainsMissingData] = useState(false);
  const [isReserveBusTabSelected, setIsReserveBusTabSelected] =
    useState<boolean>(true);
  const vehicleDate = useVehicleDate(new Date());
  const { t } = useTranslation();
  const vehicleListTabs = getVehicleListTabs(t);

  const scrollBoxRef = useRef<HTMLDivElement>(null);

  const isTrain = vehicleListTabs[vehicleTabsIndex].value === t("Tog");
  const isBus = vehicleListTabs[vehicleTabsIndex].value === t("Buss");
  const isReserveBus =
    vehicleListTabs[vehicleTabsIndex].value === t("Reservebuss");

  const [showOldVehicles, setShowOldVehicles] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const { from, to } = getISODrivingFromToTimes(vehicleDate.selectedDate);
  const searchParams = new URLSearchParams({
    to,
    from,
    countryCode: "NO",
  }).toString();

  const [reserveResults, backupResults] = useQueries({
    queries: [
      {
        queryKey: ["reserveAltTransport", "reserve", from, to],
        queryFn: async ({ signal }) => {
          const res = await axiosClient.get<z.infer<typeof schema>>(
            `${getBackendUrl()}/alternativeTransports/reserve?${searchParams}`,
            { signal },
          );
          return schema.parse(res.data);
        },
        refetchInterval: 30000,
        enabled: isReserveBus,
      },
      {
        queryKey: ["backupDetails", "backup", from, to],
        queryFn: async ({ signal }) => {
          const res = await axiosClient.get<z.infer<typeof schema>>(
            `${getBackendUrl()}/alternativeTransports/backup?${searchParams}`,
            { signal },
          );
          return schema.parse(res.data);
        },
        refetchInterval: 30000,
        enabled: isReserveBus,
      },
    ],
  });

  const { data: reserveVehiclesData, status: reserveVehiclesStatus } =
    reserveResults;
  const { data: backupVehiclesData, status: backupVehiclesStatus } =
    backupResults;

  const reserveVehicles = useMemo(
    () =>
      processTransportList({
        transportList: reserveVehiclesData?.vehicles ?? [],
        showOldVehicles,
        timeKey: "shiftEndTime",
      }),
    [reserveVehiclesData?.vehicles, showOldVehicles],
  );

  const backupVehicles = useMemo(
    () =>
      processTransportList({
        transportList: backupVehiclesData?.vehicles ?? [],
        showOldVehicles,
        timeKey: "shiftEndTime",
      }),
    [backupVehiclesData?.vehicles, showOldVehicles],
  );

  useEffect(() => {
    if (isYesterday(vehicleDate.selectedDate)) {
      setShowOldVehicles(true);
    } else if (isToday(vehicleDate.selectedDate)) {
      setShowOldVehicles(false);
    }
  }, [vehicleDate.selectedDate]);

  const showOldVehiclesBtnState = showOldVehiclesBtnStates(
    vehicleDate.selectedDate,
  );

  const reserveSectionState = useMemo(
    () => ({
      fetchStatus: reserveVehiclesStatus,
      isEmpty: reserveVehicles.length === 0,
      emptyReason: "Fant ingen reservebusser",
    }),
    [reserveVehiclesStatus, reserveVehicles.length],
  );

  const backupSectionState = useMemo(
    () => ({
      fetchStatus: backupVehiclesStatus,
      isEmpty: backupVehicles.length === 0,
      emptyReason: "Fant ingen beredskapsbusser",
    }),
    [backupVehiclesStatus, backupVehicles.length],
  );

  useEffect(() => {
    if (searchQuery.length < 2 && showTrainsMissingData) {
      setShowTrainsMissingData(false);
    }
  }, [searchQuery.length]);

  return (
    <ListContainer hasSearchQuery={searchQuery.length >= 2}>
      {isReserveBus && (
        <HStack width="100%" justifyContent="space-between" padding="12px">
          <Tabs
            defaultValue="reserve"
            fitted
            variant="accent"
            size="sm"
            onValueChange={(val) =>
              setIsReserveBusTabSelected(val.value === "reserve")
            }
          >
            <TabsList>
              <TabsTrigger value="reserve">
                Reserve ({reserveVehicles.length})
              </TabsTrigger>
              <TabsTrigger value="beredskapsbuss">
                I beredskap ({backupVehicles.length})
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <DropsButton
            justifySelf="end"
            size="sm"
            variant="secondary"
            aria-label="Legg til kjøretøy"
            leftIcon={<AddOutline24Icon />}
            onClick={() => setShowModal(true)}
          >
            Legg til
          </DropsButton>

          {showModal && (
            <AddVehicle
              isReserveBusTabSelected={isReserveBusTabSelected}
              closeModal={() => setShowModal(false)}
            />
          )}
        </HStack>
      )}

      <VStack p="12px" alignItems="space-between" gap="12px">
        <VStack alignItems="space-between" gap="0px">
          <label htmlFor="vehicle-search" style={{ margin: 0 }}>
            {vehicleListTabs[vehicleTabsIndex].label}
          </label>
          <HStack>
            <SearchInput
              id="vehicle-search"
              label={t("Søk")}
              variant="core"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onReset={() => setSearchQuery("")}
            />
            {isTrain && (
              <Field>
                <DropsDatePicker
                  label={t("Dato")}
                  variant="core"
                  width="100%"
                  defaultValue={calendarDateToday()}
                  onValueChange={(date) => {
                    if (!date) return;
                    vehicleDate.setSelectedDate(
                      date.toDate(getLocalTimeZone()),
                    );
                  }}
                  withPortal
                />
              </Field>
            )}
            <DayPicker
              showChangeDayOptions={
                vehicleListTabs[vehicleTabsIndex].value !== t("Tog")
              }
              vehicleDate={vehicleDate}
            />
          </HStack>
        </VStack>
        {searchQuery.length >= 2 && (
          <>
            <SearchResultText
              searchHits={searchHits}
              cancelSearch={() => setSearchQuery("")}
            />
            {isTrain && (
              <Checkbox
                checked={showTrainsMissingData}
                onCheckedChange={() =>
                  setShowTrainsMissingData(!showTrainsMissingData)
                }
              >
                {t("Vis tog med manglende data")}
              </Checkbox>
            )}
          </>
        )}
      </VStack>
      <Separator borderTop={`1px solid ${theme.colorOutlineSecondary}`} />
      <ScrollBox p="12px" ref={scrollBoxRef}>
        {!isTrain && (
          <FilterButtons
            showOldVehicles={showOldVehicles}
            setShowOldVehicles={setShowOldVehicles}
            disable={showOldVehiclesBtnState.disable}
            overrideCheck={showOldVehiclesBtnState.overrideCheck}
          />
        )}
        {isTrain && (
          <TrainList
            searchQuery={searchQuery}
            setSearchHits={setSearchHits}
            selectedDate={vehicleDate.selectedDate}
            scrollBoxRef={scrollBoxRef}
            showTrainsMissingData={showTrainsMissingData}
          />
        )}
        {isBus && (
          <AlternativeTransportList
            searchQuery={searchQuery}
            selectedDate={vehicleDate.selectedDate}
            showOldVehicles={showOldVehicles}
            scrollBoxRef={scrollBoxRef}
          />
        )}
        {isReserveBus && isReserveBusTabSelected && (
          <SectionStateContent state={reserveSectionState}>
            <ReserveOrBackupAlternativeTransportList
              searchQuery={searchQuery}
              showOldVehicles={showOldVehicles}
              isReserveVehicles
              vehiclesList={reserveVehicles}
            />
          </SectionStateContent>
        )}
        {isReserveBus && !isReserveBusTabSelected && (
          <SectionStateContent state={backupSectionState}>
            <ReserveOrBackupAlternativeTransportList
              searchQuery={searchQuery}
              showOldVehicles={showOldVehicles}
              isReserveVehicles={false}
              vehiclesList={backupVehicles}
            />
          </SectionStateContent>
        )}
      </ScrollBox>
    </ListContainer>
  );
};

export default VehicleList;
